import React, { useCallback, useMemo, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useMutation, useQuery } from '@apollo/client'
import AddIcon from '@mui/icons-material/Add'
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck'
import { SelectChangeEvent } from '@mui/material'
import {
  GridCellValue,
  GridColDef,
  GridRenderCellParams,
  GridRowId,
  GridRowsProp,
  GridSortModel,
} from '@mui/x-data-grid'

import Box from 'components/Common/Box'
import Button from 'components/Common/Button'
import LoadingAnimation from 'components/Common/LoadingAnimation'
import Table from 'components/Common/Table'
import Tooltip from 'components/Common/Table/Tooltip'
import Title from 'components/Common/Title'
import { Filter } from 'components/Inspection/Dashboard/FilterCard'
import DateFilter from 'components/Inspection/Listing/DateFilter'
import FieldFilter from 'components/Inspection/Listing/FieldFilter'
import FilterByCriteria from 'components/Listing/FilterByCriteria'

import { cypressAddButton } from 'constants/cypress'
import { DealerColumnField } from 'constants/dealer'
import { FilteringOption } from 'constants/Inspection/filterByCriteria'
import { routes } from 'constants/routes'
import {
  commonListColumns,
  defaultSortModel,
  selectItems,
} from 'constants/table'
import { textFiles } from 'constants/textFiles'
import { UrlParamNames } from 'constants/urlQuery'
import useLocale from 'hooks/useLocale'
import useNotification from 'hooks/useNotification'
import useQueryState from 'hooks/useQueryState'
import useTranslation from 'hooks/useTranslation'
import useUser from 'hooks/useUser'
import { CalendarRangeType } from 'models/date'
import { UserRoles } from 'models/role'
import {
  BaseIdEntity,
  FilterInputVariable,
  GenericData,
  GenericInputVariable,
  ListingFilterType,
} from 'models/services/base'
import { BackendDealer, ListDealers } from 'models/services/operations/dealer'
import { PublicationStatusMap } from 'models/status'
import { getIsoDate } from 'utils/date'
import {
  createFieldSelectItems,
  createFieldSelectLabels,
  defaultFields,
} from 'utils/Dealer/listing'
import {
  checkSearchEmptiness,
  cleanFilters,
  formatOptionsIntoFilteringOptions,
  generateFilterInput,
} from 'utils/filters'
import { formatPhoneNumber } from 'utils/phone'
import { buildDetailRoute } from 'utils/routes'
import {
  serializeFields,
  serializeFilters,
  serializePage,
  serializePageSize,
  serializeRange,
  serializeSearch,
  serializeSortModel,
} from 'utils/serializers'
import { getCarSettingsStatusList } from 'utils/status'
import { verifyParam } from 'utils/verifyUrlData'

import { GET_CITIES } from 'graphQL/Common/City/queries'
import { DELETE_DEALER } from 'graphQL/Operations/Dealer/Listing/mutations'
import { LIST_DEALERS } from 'graphQL/Operations/Dealer/Listing/queries'

import { StyledTextField } from 'styles/inspection/listing'

import { CityOption } from '../Creation'

type CustomFilterOption = {
  city: FilteringOption[]
}

const formatDealer = (
  dealers: BackendDealer[],
  table: Record<string, string>
) => {
  return dealers.map((dealer) => {
    return {
      id: dealer.id,
      name: dealer.name,
      dealerContacts:
        dealer.dealerContacts.length === 0
          ? table.notRegistered
          : dealer.dealerContacts[0].name,
      phone:
        dealer.phone.length < 10
          ? table.notRegistered
          : formatPhoneNumber(dealer.phone),
      stateName: dealer.city.state.name,
      cityName: dealer.city.name,
      createdAt: dealer.createdAt,
      status: dealer.status,
    }
  })
}

const DealerListingPage = () => {
  const { show } = useNotification()
  const { validateAllowedRoles } = useUser()
  const isAdmin = validateAllowedRoles([UserRoles.ADMIN])
  const location = useLocation()
  const { search } = location

  const defaultSortedFields = [...defaultFields].sort()

  const [selectedFields, setSelectedFields] = useQueryState<string[]>(
    UrlParamNames.FIELDS,
    (verifyParam(UrlParamNames.FIELDS, search) as string[]) ||
      defaultSortedFields,
    serializeFields,
    defaultSortedFields
  )

  const [filtersList, setFiltersList] = useQueryState<Filter[]>(
    UrlParamNames.FILTERS,
    (verifyParam(UrlParamNames.FILTERS, search) as Filter[]) || [],
    serializeFilters
  )

  const [dateRange, setDateRange] = useQueryState<CalendarRangeType>(
    UrlParamNames.DATE,
    (verifyParam(UrlParamNames.DATE, search) as CalendarRangeType) || [],
    serializeRange
  )

  const [searchValue, setSearchValue] = useQueryState<string | undefined>(
    UrlParamNames.SEARCH,
    verifyParam(UrlParamNames.SEARCH, search) as string,
    serializeSearch
  )

  const [searchInput, setSearchInput] = useState<string>(searchValue || '')

  const [pageSize, setPageSize] = useQueryState<number>(
    UrlParamNames.LIMIT,
    (verifyParam(UrlParamNames.LIMIT, search) as number) || 10,
    serializePageSize,
    10
  )
  const [page, setPage] = useQueryState<number>(
    UrlParamNames.PAGE,
    (verifyParam(UrlParamNames.PAGE, search) as number) || 1,
    serializePage
  )

  const [sortModel, setSortModel] = useQueryState<GridSortModel>(
    UrlParamNames.SORT,
    (verifyParam(UrlParamNames.SORT, search) as GridSortModel) ||
      defaultSortModel,
    serializeSortModel,
    defaultSortModel
  )
  const [filterInput, setFiltersInput] = useState<ListingFilterType>(
    generateFilterInput(filtersList)
  )

  const [selectedLanguage] = useLocale()
  const history = useHistory()

  const [deleteItem, setDeleteItem] = useState<GridRowId | null>(null)
  const { text } = useTranslation(textFiles.DEALER_LISTING)
  const { text: generalText } = useTranslation(textFiles.GENERAL)
  const [data, setData] = useState<GridRowsProp>([])
  const [dealerCount, setDealerCount] = useState<number>(0)

  const [customFilterOptions, setCustomFilterOptions] =
    useState<CustomFilterOption>({ city: [] })

  const fieldSelectItems = createFieldSelectItems(text.fieldSelect)
  const fieldSelectLabels = createFieldSelectLabels(text.fieldSelect)

  const { fromDate, toDate } = useMemo(() => {
    return {
      fromDate: getIsoDate(dateRange.fromDate),
      toDate: getIsoDate(dateRange.toDate),
    }
  }, [dateRange])

  const statusList = getCarSettingsStatusList(generalText)

  const { loading: dealerLoading, refetch: refetchDealer } = useQuery<
    ListDealers,
    FilterInputVariable
  >(LIST_DEALERS, {
    fetchPolicy: 'cache-and-network',
    variables: {
      input: {
        limit: pageSize,
        start: (page - 1) * pageSize,
        where: {
          text_search: searchValue,
          createdAt_gte: fromDate,
          createdAt_lte: toDate,
          ...cleanFilters(filterInput),
        },
        sort:
          sortModel.length > 0 && sortModel[0].sort
            ? {
                [sortModel[0].field]: sortModel[0].sort,
              }
            : undefined,
      },
    },
    onCompleted(res) {
      setData(formatDealer(res.listDealers.data, generalText.table))
      setDealerCount(res.listDealers.count)
    },
    onError() {
      show({
        updatedSeverity: 'error',
      })
    },
  })

  const { loading: citiesLoading } = useQuery<GenericData<CityOption[]>>(
    GET_CITIES,
    {
      variables: {
        input: {
          sort: {
            name: 'asc',
          },
        },
      },
      onCompleted(response) {
        setCustomFilterOptions((prevCustomFilter) => {
          return {
            ...prevCustomFilter,
            city: formatOptionsIntoFilteringOptions(response.data),
          }
        })
      },
    }
  )

  const handleShowError = () => {
    show({
      updatedSeverity: 'error',
      message: generalText.notificationText.deleteFail,
    })
  }

  const handleRefetchDealers = async () => {
    try {
      const response = await refetchDealer()

      if (response) {
        const states = response.data.listDealers
        if (states.data.length === 0 && states.count > 0) {
          setPage(1)
        }
      }
      show({
        updatedSeverity: 'success',
        message: generalText.notificationText.deleteSuccess,
      })
    } catch {
      handleShowError()
    }
  }

  const [removeDealer] = useMutation<
    GenericData<BaseIdEntity>,
    GenericInputVariable<string>
  >(DELETE_DEALER, {
    onCompleted() {
      handleRefetchDealers()
    },
    onError() {
      handleShowError()
    },
  })

  const deleteDealer = useCallback((id: GridRowId) => {
    setDeleteItem(id)
  }, [])

  const editDealer = useCallback(
    (id: GridCellValue) => {
      history.push(buildDetailRoute(id, routes.DEALER_DETAIL))
    },
    [history]
  )

  const createDealer = () => {
    history.push(routes.DEALER_CREATION)
  }

  const handleSortModelChange = (model: GridSortModel) => {
    setSortModel(model)
  }
  const handleChangeDateRange = (newDateRange: CalendarRangeType) => {
    setDateRange(newDateRange)
    setPage(1)
  }

  const handleRemoveState = (id: string) => {
    removeDealer({
      variables: {
        input: id,
      },
    })
  }

  const {
    id: idColumn,
    isoDate: dateColumn,
    status: statusColumn,
    standard: standardColumn,
    actions: actionsColumn,
  } = commonListColumns(
    PublicationStatusMap,
    selectedLanguage,
    generalText,
    editDealer,
    isAdmin ? deleteDealer : undefined,
    statusList
  )
  const [columns, setColumns] = useState<GridColDef[]>([
    { ...idColumn, hide: !selectedFields.includes(idColumn.field) },
    {
      ...standardColumn,
      field: DealerColumnField.NAME,
      headerName: text.table.name,
      hide: !selectedFields.includes(DealerColumnField.NAME),

      renderCell: (params: GridRenderCellParams) => {
        return <Tooltip>{params.row.name}</Tooltip>
      },
    },
    {
      ...standardColumn,
      field: DealerColumnField.CONTACT_NAME,
      headerName: text.table.dealerContacts,
      hide: !selectedFields.includes(DealerColumnField.CONTACT_NAME),
      renderCell: (params: GridRenderCellParams) => {
        return <Tooltip>{params.row.dealerContacts}</Tooltip>
      },
    },
    {
      ...standardColumn,
      field: DealerColumnField.DEALER_PHONE,
      headerName: text.table.telephoneNumber,
      hide: !selectedFields.includes(DealerColumnField.DEALER_PHONE),
      renderCell: (params: GridRenderCellParams) => {
        return <Tooltip>{params.row.phone}</Tooltip>
      },
    },
    {
      ...standardColumn,
      field: DealerColumnField.STATE_NAME,
      sortable: false,
      headerName: text.table.stateName,
      hide: !selectedFields.includes(DealerColumnField.STATE_NAME),

      renderCell: (params: GridRenderCellParams) => {
        return <Tooltip>{params.row.stateName}</Tooltip>
      },
    },
    {
      ...standardColumn,
      field: DealerColumnField.CITY_NAME,
      headerName: text.table.cityName,
      hide: !selectedFields.includes(DealerColumnField.CITY_NAME),
      renderCell: (params: GridRenderCellParams) => {
        return <Tooltip>{params.row.cityName}</Tooltip>
      },
    },
    {
      ...dateColumn,
      field: DealerColumnField.CREATED_AT,
      headerName: text.table.createdAt,
      hide: !selectedFields.includes(DealerColumnField.CREATED_AT),
    },
    {
      ...statusColumn,
      sortable: true,
      hide: !selectedFields.includes(statusColumn.field),
    },
    {
      ...actionsColumn,
      hide: !selectedFields.includes(actionsColumn.field),
    },
  ])

  const handleFiltersList = (
    newFiltersList: Filter[],
    newFilterInput: ListingFilterType
  ) => {
    setFiltersList(newFiltersList)
    setFiltersInput(newFilterInput)
    setPage(1)
  }

  const handleSearchChange = (
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    setSearchInput(event.target.value)
  }

  const submitSearchValue = () => {
    setPage(1)
    const newValue = checkSearchEmptiness(searchInput)
    setSearchValue(newValue)
  }

  const handleSelectItem = (valueInput: string) => {
    setColumns((prevColumns) =>
      prevColumns.map((column) => {
        return column.field === valueInput
          ? { ...column, hide: !column.hide }
          : column
      })
    )
    setSelectedFields((prevFields) => {
      if (prevFields.includes(valueInput)) {
        return prevFields.filter((field) => field !== valueInput)
      }
      return [...prevFields, valueInput]
    })
  }

  const handleResetDefault = () => {
    setSelectedFields(defaultFields)
    setColumns((prevColumns) =>
      prevColumns.map((column) => {
        const fieldExist = defaultFields.some(
          (defaultField) => column.field === defaultField
        )
        return { ...column, hide: !fieldExist }
      })
    )
  }

  const handleChangePage = (
    event: React.ChangeEvent<unknown>,
    newPage: number
  ) => {
    setPage(newPage)
  }

  const handleChangePageSize = (event: SelectChangeEvent<number>) => {
    setPageSize(event.target.value as number)
  }

  const getPageCount = () => {
    return Math.ceil(dealerCount / pageSize)
  }

  // const handleRemoveItem = (id: string) => {
  //   // eslint-disable-next-line no-console
  //   console.log('id', id)
  // }

  // const handleRemoveMultipleItems = async (ids: string[]) => {
  //   // eslint-disable-next-line no-console
  //   console.log('IDS', ids)
  // }

  if (citiesLoading) return <LoadingAnimation showAnimation={citiesLoading} />

  return (
    <Box width="100%">
      <Title
        icon={<PlaylistAddCheckIcon />}
        subtitle={`${dealerCount} ${text.description}`}
        title={text.title}
      />
      <Box
        alignItems="center"
        display="flex"
        justifyContent="space-between"
        marginTop="2rem"
        width="100%"
      >
        <Button
          startIcon={<AddIcon />}
          size="medium"
          onClick={() => createDealer()}
          style={{ minWidth: '160px', marginRight: '20px' }}
          data-cy={cypressAddButton}
        >
          {text.addButton}
        </Button>

        <Box display="flex" flex="1 0.8 auto" justifyContent="flex-end">
          <Box marginRight="1rem" flex="0 1 430px">
            <StyledTextField
              placeholder={text.searchPlaceholder}
              fullWidth
              value={searchInput}
              onChange={handleSearchChange}
              submitFunction={submitSearchValue}
              name="search"
            />
          </Box>
          <Box marginRight="1rem">
            <FilterByCriteria
              filtersList={filtersList}
              handleFiltersList={handleFiltersList}
              file={textFiles.DEALER_LISTING}
              filterInput={filterInput}
              statusList={statusList}
              options={customFilterOptions}
              staticFields={['status', 'city']}
            />
          </Box>
          <Box marginRight="1rem">
            <FieldFilter
              handleSelectItem={handleSelectItem}
              items={fieldSelectItems}
              selectedValues={selectedFields}
              handleResetDefault={handleResetDefault}
            />
          </Box>
          <Box>
            <DateFilter
              dateRange={dateRange}
              handleChangeDateRange={handleChangeDateRange}
              title={text.dateRangeTitle}
              maxDate={null}
            />
          </Box>
        </Box>
      </Box>
      <Table
        columns={columns}
        dateRange={dateRange}
        searchValue={searchValue}
        data={data}
        currentPage={page}
        onPageChange={handleChangePage}
        onSelectChange={handleChangePageSize}
        pageSize={pageSize}
        selectItems={selectItems}
        pageCount={getPageCount()}
        filtersList={filtersList}
        fields={selectedFields}
        fieldLabels={fieldSelectLabels}
        deletedItemId={deleteItem}
        setDeleteItemId={setDeleteItem}
        route={routes.DEALER_DETAIL}
        sortModel={sortModel}
        handleSortModelChange={handleSortModelChange}
        loading={dealerLoading}
        handleRemoveItem={handleRemoveState}
        hideDownloadModal
        checkboxSelection={false}
      />
    </Box>
  )
}
export default DealerListingPage
